import React, { useRef, useState, useEffect } from "react";
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar } from "recharts";
import { formatValue } from "@/lib/chartTheme";
import { buildCondEval } from "@/lib/condFormat";
import { CondIcon } from "@/components/bi/CondIcon";
import { t } from "@/lib/i18n";
import api from "@/lib/api";
import {
  TrendingUp, TrendingDown, Target, DollarSign, Users, ShoppingCart, Activity, Zap, Award, Star, Heart,
  BarChart3, Globe, Truck, Package, AlertTriangle, Gauge,
} from "lucide-react";

export const KPI_ICONS = {
  target: Target, dollar: DollarSign, users: Users, cart: ShoppingCart, trending: TrendingUp,
  activity: Activity, zap: Zap, award: Award, star: Star, heart: Heart, chart: BarChart3,
  globe: Globe, truck: Truck, package: Package, alert: AlertTriangle, gauge: Gauge,
};

const EMPTY_TOKENS = { dash: "\u2014", zero: "0", blank: "" };
const VALUE_SIZE = { md: "text-3xl md:text-4xl", lg: "text-4xl md:text-5xl", xl: "text-5xl md:text-6xl" };
const STATUS_COLOR = { good: "var(--success, #00C2A8)", warn: "#F59E0B", bad: "var(--danger, #E85D6A)" };

function agg(rows, key) {
  if (!key) return null;
  const vals = rows.map((r) => Number(r[key])).filter(Number.isFinite);
  return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
}

// Item 22: independent target source — its own field + aggregation (optionally its own
// dataset and ignoring dashboard filters), fetched separately from the actual.
function useQueryTarget(tgtCfg, datasetId, publicToken, filters) {
  const enabled = tgtCfg?.mode === "query" && !!tgtCfg.field && !!(tgtCfg.dataset_id || datasetId);
  const dsId = tgtCfg?.dataset_id || datasetId;
  const effFilters = tgtCfg?.respectFilters === false ? [] : (filters || [])
    .filter((f) => f.field && f.value != null && f.value !== "" && !(Array.isArray(f.value) && !f.value.length))
    .map((f) => ({ field: f.field, op: f.op || "in", value: f.value, ...(f.date_part && f.date_part !== "none" ? { date_part: f.date_part } : {}) }));
  const key = JSON.stringify(enabled ? { f: tgtCfg.field, a: tgtCfg.agg || "sum", d: dsId, fl: effFilters, p: publicToken || null } : null);
  const [val, setVal] = useState(null);
  useEffect(() => {
    if (!enabled) { setVal(null); return; }
    let active = true;
    const q = { dimensions: [], measures: [], adhoc_measures: [{ field: tgtCfg.field, agg: tgtCfg.agg || "sum", alias: "target_value" }], filters: effFilters, sort: [], limit: 1 };
    const p = publicToken
      ? api.post("/public/model/query", { token: publicToken, dataset_id: dsId, semantic_query: q }, { timeout: 30000 })
      : api.post(`/datasets/${dsId}/model/query`, q, { timeout: 30000 });
    p.then(({ data: d }) => { if (active) { const n = Number(d?.rows?.[0]?.target_value); setVal(Number.isFinite(n) ? n : null); } })
      .catch(() => { if (active) setVal(null); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return enabled ? val : null;
}

function Spark({ data, style, color }) {
  const c = color || "#2B4EFF";
  const gid = `kg-${c.replace(/[^\w]/g, "")}`;
  if (style === "bar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}><Bar dataKey="v" fill={c} radius={[2, 2, 0, 0]} isAnimationActive={false} /></BarChart>
      </ResponsiveContainer>
    );
  }
  if (style === "line") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}><Line type="monotone" dataKey="v" stroke={c} strokeWidth={2} dot={false} isAnimationActive={false} /></LineChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data}>
        <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={c} stopOpacity={0.28} /><stop offset="100%" stopColor={c} stopOpacity={0} /></linearGradient></defs>
        <Area type="monotone" dataKey="v" stroke={c} strokeWidth={2} fill={`url(#${gid})`} dot={false} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function Indicator({ style, status, delta }) {
  if (!status || style === "none") return null;
  const color = STATUS_COLOR[status];
  if (style === "traffic") {
    return (
      <span data-testid="kpi-indicator" className="flex items-center gap-1 mb-1.5" title={`Status: ${status}`}>
        {["bad", "warn", "good"].map((s) => (
          <span key={s} className="w-2.5 h-2.5 rounded-full border border-[color:var(--glass-border)]"
            style={{ background: s === status ? STATUS_COLOR[s] : "transparent", opacity: s === status ? 1 : 0.35 }} />
        ))}
      </span>
    );
  }
  if (style === "badge") {
    const txt = { good: "On track", warn: "At risk", bad: "Off track" }[status];
    return <span data-testid="kpi-indicator" style={{ color, background: `color-mix(in srgb, ${color} 14%, transparent)` }} className="text-[10px] font-semibold px-2 py-0.5 rounded-full mb-1.5">{txt}</span>;
  }
  const up = delta == null ? status === "good" : delta >= 0;
  const I = up ? TrendingUp : TrendingDown;
  return <I data-testid="kpi-indicator" className="w-4 h-4 mb-1.5" style={{ color }} />;
}

function KpiIcon({ k, compact = false }) {
  if (!k?.show) return null;
  // Item 23: the icon is a sized box inside the KPI content group (never absolutely positioned),
  // so Left/Right stay glued to the value and it scales with the configured size.
  // Item 5: inside a card grid the box also shrinks with the cell so it never clips.
  const box = Math.max(14, Math.min(96, Number(k.size) || 32));
  const glyph = Math.round(box * 0.56);
  const sizeStyle = compact
    ? { width: `min(${box}px, 22cqi)`, height: `min(${box}px, 22cqi)` }
    : { width: box, height: box };
  if (k.image) {
    return <img src={k.image} alt="" data-testid="kpi-icon" className="rounded-lg shrink-0 self-center"
      style={{ ...sizeStyle, objectFit: k.fit || "cover" }} />;
  }
  const I = KPI_ICONS[k.name || "target"] || Target;
  return (
    <span data-testid="kpi-icon" className="rounded-lg flex items-center justify-center shrink-0 self-center"
      style={{ ...sizeStyle, background: `color-mix(in srgb, ${k.color || "#2B4EFF"} 12%, transparent)` }}>
      <I style={{ color: k.color || "#2B4EFF", ...(compact ? { width: "58%", height: "58%" } : { width: glyph, height: glyph }) }} />
    </span>
  );
}

// KPI widget (items 22/23/66-73): actual/target with independent sources, comparisons,
// conditional colors, icon alignment, indicators, sparklines, layouts and formatting.
// Item 5: a single KPI widget can render an internal Row × Column card grid — one card per
// category when a dimension is assigned, otherwise one card per measure.
export function KpiView(props) {
  const { data, config } = props;
  const k = config.options?.kpi || {};
  const grid = k.grid || {};
  const rowsN = Math.max(1, Math.min(6, Number(grid.rows) || 1));
  const colsN = Math.max(1, Math.min(6, Number(grid.cols) || 1));
  const cells = rowsN * colsN;
  const instances = kpiInstances(data, config, cells);
  if (cells <= 1 || instances.length <= 1) return <KpiCardView {...props} />;
  return (
    <div data-testid="kpi-card-grid" data-rows={rowsN} data-cols={colsN}
      className="h-full w-full grid gap-1.5 p-1 overflow-auto"
      style={{
        gridTemplateColumns: `repeat(${colsN}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${Math.max(rowsN, Math.ceil(instances.length / colsN))}, minmax(68px, 1fr))`,
      }}>
      {instances.map((inst, i) => (
        <div key={inst.id} data-testid={`kpi-card-cell-${i}`}
          className="min-w-0 min-h-0 overflow-hidden rounded-[var(--radius-control)] border border-[color:var(--glass-border)] bg-[color:var(--surface)]">
          <KpiCardView {...props} data={inst.data} config={inst.config} labelOverride={inst.label} compact />
        </div>
      ))}
    </div>
  );
}

// One card per category (dimension assigned) or per measure (several measures, no dimension).
function kpiInstances(data, config, cells) {
  const rows = data?.rows || [];
  const cols = data?.columns || [];
  const measuresMeta = data?.meta?.measures || [];
  const measureKeys = measuresMeta.length
    ? measuresMeta.map((m) => m.key).filter(Boolean)
    : cols.filter((c) => /^(sum|avg|count|count_distinct|counta|min|max|median|product|stddev|var|p25|p75)/.test(c));
  const dimKeys = (data?.meta?.dimensions || []).map((d) => d.key).filter(Boolean);
  const dimCol = dimKeys[0] || cols.find((c) => !measureKeys.includes(c));
  if (dimCol && rows.length > 1) {
    return rows.slice(0, cells).map((r, i) => ({
      id: `${String(r[dimCol])}-${i}`,
      label: String(r[dimCol] ?? "—"),
      data: { ...data, rows: [r] },
      config,
    }));
  }
  if (measureKeys.length > 1) {
    return measureKeys.slice(0, cells).map((mk, i) => ({
      id: mk,
      label: measuresMeta.find((m) => m.key === mk)?.label || mk.replace(/_/g, " "),
      data: {
        ...data,
        columns: [mk],
        rows: rows.length ? [{ [mk]: rows.reduce((acc, r) => acc + (Number(r[mk]) || 0), 0) }] : [],
        meta: { ...(data.meta || {}), measures: measuresMeta.filter((m) => m.key === mk) },
      },
      // a per-measure card has no second measure, so target-by-measure stays on the parent config
      config,
    }));
  }
  return [];
}

function KpiCardView({ data, config, datasetId = null, publicToken = null, filters = [], baselineData = null, labelOverride = null, compact = false }) {
  const tiltRef = useRef(null);
  const onMove = (e) => {
    const el = tiltRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    el.style.setProperty("--ry", `${(px - 0.5) * 10}deg`);
    el.style.setProperty("--rx", `${(0.5 - py) * 10}deg`);
    el.style.setProperty("--mx", `${px * 100}%`);
    el.style.setProperty("--my", `${py * 100}%`);
    el.style.setProperty("--gloss", "0.18");
  };
  const onLeave = () => {
    const el = tiltRef.current; if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
    el.style.setProperty("--gloss", "0");
  };

  const k = config.options?.kpi || {};
  const loc = config.options?.locale || undefined;
  const fmtV = (v, f) => formatValue(v, f, loc);
  const tgtCfg = k.target || {};
  const queryTarget = useQueryTarget(tgtCfg, datasetId, publicToken, filters);
  const rows = data.rows;
  const measuresMeta = data.meta?.measures || [];
  const mMeta = measuresMeta[0];
  const measureKey = mMeta?.key
    || data.columns.find((c) => /^(sum|avg|count|min|max|median|product|stddev|var)/.test(c))
    || data.columns[data.columns.length - 1];
  if (!measureKey) {
    return (
      <div data-testid="kpi-no-measure" className="h-full w-full flex items-center justify-center px-3 text-center text-xs text-[color:var(--ink-3)]">
        No measure data — check that this KPI's measure still exists in the semantic model.
      </div>
    );
  }
  const m1Key = measuresMeta[1]?.key || data.columns.find((c) => c !== measureKey && /^(sum|avg|count|min|max|median|product|stddev|var)/.test(c)) || null;
  const label = labelOverride || mMeta?.label || measureKey.replace(/_/g, " ");
  const fmt = mMeta?.format;
  const compare = config.options?.compare;
  const dimCol = data.columns.find((c) => c !== measureKey && c !== m1Key);
  let sorted = rows;
  if (compare && dimCol) sorted = [...rows].sort((a, b) => String(a[dimCol] ?? "").localeCompare(String(b[dimCol] ?? "")));
  const hasTrend = sorted.length > 1;

  let bigValue, delta = null, deltaLabel = null;
  if (compare && hasTrend) {
    bigValue = sorted[sorted.length - 1][measureKey];
    const prev = Number(sorted[sorted.length - 2][measureKey]);
    if (prev !== 0 && Number.isFinite(prev)) delta = ((Number(bigValue) - prev) / Math.abs(prev)) * 100;
    deltaLabel = `vs previous ${compare.period || "period"}`;
  } else if (hasTrend) {
    bigValue = sorted.reduce((acc, r) => acc + (Number(r[measureKey]) || 0), 0);
  } else {
    bigValue = sorted[0]?.[measureKey];
  }

  // Batch 8: baseline value from the what-if compare query (same aggregation logic)
  let baseValue = null;
  const bRows = baselineData?.rows || [];
  if (bRows.length) {
    let bs = bRows;
    if (compare && dimCol) bs = [...bRows].sort((a, b) => String(a[dimCol] ?? "").localeCompare(String(b[dimCol] ?? "")));
    if (compare && bs.length > 1) baseValue = bs[bs.length - 1][measureKey];
    else if (bs.length > 1) baseValue = bs.reduce((acc, r) => acc + (Number(r[measureKey]) || 0), 0);
    else baseValue = bs[0]?.[measureKey];
  }

  // Item 22: target sources — fixed value, second measure, or fully independent query
  const actualN = Number(bigValue);
  let target = null;
  if (tgtCfg.mode === "static" && tgtCfg.value !== "" && tgtCfg.value != null) target = Number(tgtCfg.value);
  else if (tgtCfg.mode === "measure" && m1Key) target = hasTrend ? agg(sorted, m1Key) : Number(sorted[0]?.[m1Key]);
  else if (tgtCfg.mode === "query") target = queryTarget;
  const variance = target != null && Number.isFinite(actualN) ? actualN - target : null;
  const achievement = target ? (actualN / target) * 100 : null;

  const benchCfg = k.benchmark || {};
  let bench = null, benchDelta = null;
  if (benchCfg.mode === "static" && benchCfg.value !== "" && benchCfg.value != null) bench = Number(benchCfg.value);
  else if (benchCfg.mode === "measure" && m1Key) bench = hasTrend ? agg(sorted, m1Key) : Number(sorted[0]?.[m1Key]);
  if (bench != null && bench !== 0 && Number.isFinite(actualN)) benchDelta = ((actualN - bench) / Math.abs(bench)) * 100;
  if (benchDelta != null && delta == null) { delta = benchDelta; deltaLabel = benchCfg.label || "vs benchmark"; }

  // sparkline (item 72)
  const sk = k.sparkline || {};
  const maxPts = Number(sk.points) > 1 ? Number(sk.points) : 60;
  let spark = hasTrend ? sorted.slice(-maxPts).map((r, i) => ({ i, v: Number(r[measureKey]) || 0 })) : [];
  if (sk.show === false) spark = [];
  if (!compare && bench == null && spark.length >= 2 && delta == null) {
    const first = spark[0].v, last = spark[spark.length - 1].v;
    if (first !== 0) delta = ((last - first) / Math.abs(first)) * 100;
  }

  // status (item 70)
  const ind = k.indicator || {};
  const higherBetter = ind.higherIsBetter !== false;
  let status = null;
  if (achievement != null) {
    const goodAt = Number(ind.goodAt) || 100, warnAt = Number(ind.warnAt) || 80;
    const pct = higherBetter ? achievement : (target !== 0 ? (2 - actualN / target) * 100 : achievement);
    status = pct >= goodAt ? "good" : pct >= warnAt ? "warn" : "bad";
  } else if (delta != null) {
    status = (higherBetter ? delta >= 0 : delta <= 0) ? "good" : "bad";
  }

  const ev = buildCondEval(config.options?.condRules, rows);
  const lastRow = sorted[sorted.length - 1] || {};
  const kfx = ev ? (ev.cell({ ...lastRow, [measureKey]: bigValue }, measureKey) || ev.cell({ value: bigValue }, "value")) : null;
  const outline = ev?.widgetOutline();

  const emptyTok = EMPTY_TOKENS[config.options?.emptyAs || "dash"];
  const layout = k.layout || "classic";
  const S = k.sections || {};
  const sec = (name) => S[name] || {};
  const posOf = (name, def) => sec(name).position || def;
  const fontOf = (name, extra = {}) => {
    const s = sec(name);
    const st = { ...extra };
    if (s.fontSize) st.fontSize = Number(s.fontSize);
    if (s.weight) st.fontWeight = Number(s.weight);
    if (s.color) st.color = s.color;
    if (s.family) st.fontFamily = s.family;        // item 22: independent font family
    if (s.align) st.textAlign = s.align;           // item 22: independent horizontal alignment
    return st;
  };
  const center = k.align === "center" || layout === "hero";
  // Item 22: per-section horizontal / vertical alignment (value drives the card's content axis)
  const H_JUSTIFY = { left: "justify-start", center: "justify-center", right: "justify-end" };
  const V_JUSTIFY = { top: "justify-start", center: "justify-center", bottom: "justify-end" };
  const valAlign = sec("value").align || (center ? "center" : null);
  const valRowJustify = H_JUSTIFY[valAlign] || "";
  const cardVJustify = V_JUSTIFY[sec("value").valign] || "justify-center";
  // Item 23: icon position is a first-class layout slot (left | right | top | top-right)
  const iconPos = k.icon?.show ? (sec("icon").position || k.icon?.position || "left") : "hidden";
  const valueCls = sec("value").fontSize ? "" : VALUE_SIZE[layout === "compact" ? "md" : k.valueSize || "md"];
  // Auto-fit: without an explicit size the value scales with the card width (never "7…")
  const VALUE_MAX = { md: 36, lg: 48, xl: 60 };
  const autoFit = !sec("value").fontSize
    ? { fontSize: compact
      ? "clamp(12px, min(13cqi, 26cqh), 30px)"
      : `clamp(14px, 13cqi, ${VALUE_MAX[layout === "compact" ? "md" : k.valueSize || "md"]}px)` }
    : {};
  // Item 22: target-driven conditional value color (cond-format rules still win)
  const tgtColor = target != null && Number.isFinite(actualN)
    ? (higherBetter ? actualN >= target : actualN <= target) ? (tgtCfg.aboveColor || null) : (tgtCfg.belowColor || null)
    : null;
  const valueStyle = fontOf("value", { ...autoFit, color: kfx?.color || tgtColor || sec("value").color || k.valueColor || undefined });
  const labelStyle = fontOf("label", { color: sec("label").color || k.labelColor || undefined });
  const goodDelta = higherBetter ? (delta ?? 0) >= 0 : (delta ?? 0) <= 0;
  const cmp = sec("comparison");
  const goodColor = cmp.goodColor || "var(--success)";
  const badColor = cmp.badColor || "var(--danger)";

  const valueNode = (
    <div data-testid="kpi-value" style={valueStyle} className={`font-display ${valueCls} tracking-tight tabular-nums text-[color:var(--ink)] leading-none min-w-0 truncate`}>
      {kfx?.icon && <CondIcon fx={kfx} size="w-6 h-6" />}
      {bigValue == null ? emptyTok : fmtV(bigValue, fmt)}
    </div>
  );
  const deltaChip = delta !== null && posOf("comparison", "inline") !== "hidden" && (
    <div data-testid="kpi-delta" style={fontOf("comparison", { color: goodDelta ? goodColor : badColor, background: `color-mix(in srgb, ${goodDelta ? goodColor : badColor} 12%, transparent)` })}
      className="inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full shrink-0">
      {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{Math.abs(delta).toFixed(1)}%
    </div>
  );
  const labelPos = posOf("label", "below");
  const labelNode = labelPos !== "hidden" && (
    <div data-testid="kpi-label" style={labelStyle} className={`text-xs text-[color:var(--ink-3)] truncate ${labelPos === "above" ? "mb-0.5" : "mt-0.5"}`}>{label}{deltaLabel && <span className="opacity-80"> · {deltaLabel}</span>}</div>
  );
  // Item 22: target section (label + value + progress) and comparison section (achieved % / variance)
  const compareAs = tgtCfg.compareAs || "both";
  const tgtLabel = tgtCfg.label || t("target");
  const barPct = Math.max(2, Math.min(100, achievement || 0));
  const bulletScale = Math.max(achievement || 0, 120);
  const targetPos = target != null ? posOf("target", "below") : "hidden";
  const tsec = sec("target");
  const showBar = layout !== "compact" && (tsec.showBar ?? ((tgtCfg.display || "bar") !== "none"));
  const barStyle = tsec.barStyle || tgtCfg.display || "bar";
  const targetNode = targetPos !== "hidden" && (
    <div data-testid="kpi-target-row" className={"min-w-0 " + (targetPos === "right" ? "w-full" : "mt-2")}>
      {showBar && barStyle !== "none" && (
        <div className="relative rounded-full bg-[color:var(--surface-2)] overflow-visible" style={{ height: Number(tsec.barHeight) || 6 }}>
          {barStyle === "bullet" ? (
            <>
              <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(2, Math.min(100, ((achievement || 0) / bulletScale) * 100))}%`, background: tsec.barColor || STATUS_COLOR[status || "good"] }} />
              <div data-testid="kpi-bullet-tick" className="absolute -top-1 h-3.5 w-0.5 rounded bg-[color:var(--ink-2)]" style={{ left: `${(100 / bulletScale) * 100}%` }} title={`${tgtLabel}: ${fmtV(target, fmt)}`} />
            </>
          ) : (
            <div className="h-full rounded-full transition-all" style={{ width: `${barPct}%`, background: tsec.barColor || STATUS_COLOR[status || "good"] }} />
          )}
        </div>
      )}
      <div style={fontOf("target")} className={"flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-[color:var(--ink-3)] min-w-0 " + (H_JUSTIFY[tsec.align] || "")}>
        <span className="truncate">
          <span data-testid="kpi-target-label" style={{ ...(tsec.labelColor ? { color: tsec.labelColor } : {}), ...(tsec.labelFontSize ? { fontSize: Number(tsec.labelFontSize) } : {}) }}>{tgtLabel}</span>{" "}
          <b data-testid="kpi-target-value" className="text-[color:var(--ink-2)]"
            style={{
              ...(tsec.valueColor ? { color: tsec.valueColor } : {}),
              ...(tsec.valueFontSize ? { fontSize: Number(tsec.valueFontSize) } : {}),
              ...(tsec.valueWeight ? { fontWeight: Number(tsec.valueWeight) } : {}),
              ...(tsec.valueFamily ? { fontFamily: tsec.valueFamily } : {}),
            }}>{fmtV(target, fmt)}</b>
        </span>
      </div>
    </div>
  );
  const cmpPos = posOf("comparison", "inline");
  // Item 22: "None" hides achievement/variance entirely — no empty placeholder is rendered.
  const comparisonNode = target != null && cmpPos !== "hidden" && compareAs !== "none" && (
    <div data-testid="kpi-comparison" style={fontOf("comparison")} className={"flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[color:var(--ink-3)] min-w-0 " + (cmpPos === "below" ? "mt-1 " : "") + (H_JUSTIFY[sec("comparison").align] || "")}>
      {compareAs !== "absolute" && (
        <span className="whitespace-nowrap">{t("achieved")} <b style={{ color: STATUS_COLOR[status || "good"] }}>{achievement != null ? `${achievement.toFixed(achievement >= 1000 ? 0 : 1)}%` : "—"}</b></span>
      )}
      {compareAs !== "percent" && (
        <span className="whitespace-nowrap">Δ <b style={{ color: variance != null ? (variance >= 0 === higherBetter ? goodColor : badColor) : undefined }}>{variance != null ? fmtV(variance, fmt) : "—"}</b></span>
      )}
    </div>
  );
  const sparkPos = spark.length > 1 && layout !== "compact" && !compact ? (sec("spark").position || (layout === "split" ? "right" : "bottom")) : "hidden";
  const sparkH = Number(sec("spark").height) || 40;
  const sparkNode = sparkPos !== "hidden" && (
    <div data-testid="kpi-sparkline" className={sparkPos === "right" ? "flex-1 min-w-[80px] h-full py-1" : sparkPos === "background" ? "absolute inset-x-0 bottom-0 opacity-40 pointer-events-none" : "mt-2 -mx-1"}
      style={sparkPos === "right" ? undefined : { height: sparkH }}>
      <Spark data={spark} style={sk.style || "area"} color={sk.color} />
    </div>
  );
  const indStyle = ind.style || "arrow";
  const indPos = status && indStyle !== "none" ? posOf("indicator", indStyle === "arrow" ? "inline" : "top") : "hidden";
  const indicatorNode = indPos !== "hidden" && (
    <span data-testid="kpi-indicator-slot" style={sec("indicator").fontSize ? { fontSize: Number(sec("indicator").fontSize) } : undefined} className="flex items-center shrink-0 [&>*]:mb-0">
      <Indicator style={indStyle} status={status} delta={delta} />
    </span>
  );
  const iconNode = iconPos !== "hidden" && <KpiIcon k={k.icon} compact={compact} />;

  // Batch 8: baseline row (what-if compare) — baseline value + Δ vs current scenario
  const baseN = Number(baseValue);
  const baseDelta = baseValue != null && Number.isFinite(baseN) && baseN !== 0 && Number.isFinite(actualN)
    ? ((actualN - baseN) / Math.abs(baseN)) * 100 : null;
  const baselineRow = baseValue != null && (
    <div data-testid="kpi-baseline-row" className="mt-1.5 flex items-center gap-2 text-[11px] text-[color:var(--ink-3)] min-w-0">
      <span className="px-1.5 py-0.5 rounded-full bg-[color:var(--surface-2)] font-medium">Baseline</span>
      <b className="text-[color:var(--ink-2)] tabular-nums">{fmtV(baseValue, fmt)}</b>
      {baseDelta != null && (
        <span data-testid="kpi-baseline-delta" className={"font-semibold " + ((higherBetter ? baseDelta >= 0 : baseDelta <= 0) ? "text-[color:var(--success)]" : "text-[color:var(--danger)]")}>
          {baseDelta >= 0 ? "+" : ""}{baseDelta.toFixed(1)}%
        </span>
      )}
    </div>
  );

  const wrapStyle = { containerType: compact ? "size" : "inline-size", ...(outline ? { boxShadow: `inset 0 0 0 2px ${outline}`, borderRadius: 12 } : {}) };

  if (layout === "compact") {
    return (
      <div ref={tiltRef} onMouseMove={onMove} onMouseLeave={onLeave} style={wrapStyle} data-kpi-layout="compact" className="tilt-3d relative h-full w-full flex items-center gap-2.5 px-2 rounded-[var(--radius-control)] overflow-hidden">
        {iconPos === "top" && iconNode}
        <div className="min-w-0 flex-1">
          {labelPos === "above" && labelNode}
          <div data-testid="kpi-value-row" className={`flex items-end gap-2 min-w-0 ${valRowJustify}`}>{indPos === "left" && indicatorNode}{iconPos === "left" && iconNode}{valueNode}{iconPos === "right" && iconNode}{indPos === "inline" && indicatorNode}{cmpPos === "inline" && deltaChip}</div>
          {labelPos === "below" && labelNode}
          {cmpPos !== "inline" && cmpPos !== "hidden" && comparisonNode}
          {targetPos !== "hidden" && targetNode}
          {baselineRow}
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <div className="flex flex-col items-end gap-0.5">
            {indPos !== "inline" && indPos !== "left" && indicatorNode}
            {cmpPos !== "inline" && deltaChip}
          </div>
          {iconPos === "top-right" && iconNode}
        </div>
      </div>
    );
  }

  // Section-based composer (classic / hero / split / custom): every slot has an explicit position.
  // Item 23: left/right icons live INSIDE the value row (glued to the value); only "top-right"
  // docks to the card corner.
  const rightCol = [
    iconPos === "top-right" ? <span key="ic" className="self-start">{iconNode}</span> : null,
    indPos === "right" ? <span key="ind">{indicatorNode}</span> : null,
    cmpPos === "right" ? <span key="cmp" className="min-w-0">{comparisonNode}</span> : null,
    targetPos === "right" ? <span key="tgt" className="min-w-0 w-full">{targetNode}</span> : null,
  ].filter(Boolean);
  const showTopRow = iconPos === "top" || indPos === "top";
  const mainAlign = center ? "items-center text-center" : "";
  return (
    <div ref={tiltRef} onMouseMove={onMove} onMouseLeave={onLeave} style={wrapStyle} data-kpi-layout={layout}
      className={`tilt-3d relative h-full w-full rounded-[var(--radius-control)] flex flex-col ${cardVJustify} px-2 overflow-hidden ${mainAlign}`}>
      {showTopRow && (
        <div data-testid="kpi-top-row" className={`flex items-center gap-2 mb-1.5 w-full ${center ? "justify-center" : ""}`}>
          {iconPos === "top" && iconNode}
          {indPos === "top" && indicatorNode}
        </div>
      )}
      <div className={`flex items-center gap-3 w-full min-w-0 ${center && !rightCol.length ? "justify-center" : ""}`}>
        <div className={`min-w-0 flex-1 flex flex-col ${center ? "items-center text-center" : ""}`}>
          {labelPos === "above" && labelNode}
          <div data-testid="kpi-value-row" className={`flex items-end gap-2 min-w-0 flex-wrap ${valRowJustify}`}>
            {indPos === "left" && indicatorNode}
            {iconPos === "left" && iconNode}
            {valueNode}
            {iconPos === "right" && iconNode}
            {indPos === "inline" && indicatorNode}
            {cmpPos === "inline" && deltaChip}
            {cmpPos === "inline" && comparisonNode}
          </div>
          {labelPos === "below" && labelNode}
          {cmpPos === "below" && (deltaChip || comparisonNode) && <div className="flex items-center gap-2 flex-wrap mt-1">{deltaChip}{comparisonNode}</div>}
          {targetPos === "below" && targetNode}
          {baselineRow}
        </div>
        {sparkPos === "right" && sparkNode}
        {rightCol.length > 0 && (
          <div data-testid="kpi-right-col" className="shrink-0 max-w-[45%] flex flex-col items-end gap-1.5 min-w-0">{rightCol}</div>
        )}
      </div>
      {sparkPos !== "right" && sparkNode}
    </div>
  );
}
